/**
 * Manual job run with optional dry-run.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/app/store';
import { runJobThunk, selectRunningJob } from '@/app/store/jobsSlice';
import type { Job } from '@/app/types/jobs';
import { Modal, Button } from '@/components/common/Modal';

import './JobRunNowModal.scss';

export interface JobRunNowModalProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
}

export const JobRunNowModal: React.FC<JobRunNowModalProps> = ({ job, isOpen, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [dryRun, setDryRun] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isRunning = useSelector((state: RootState) =>
    job != null ? selectRunningJob(state) === job.job_name : false
  );

  useEffect(() => {
    if (isOpen) {
      setDryRun(true);
      setSubmitError(null);
    }
  }, [isOpen, job?.job_name]);

  useEffect(() => {
    if (job != null && !job.enabled) {
      setDryRun(true);
    }
  }, [job, job?.enabled]);

  const dryRunLocked = job != null && !job.enabled;
  const effectiveDryRun = dryRunLocked ? true : dryRun;

  const handleSubmit = async () => {
    if (job == null) return;
    setSubmitError(null);
    try {
      await dispatch(
        runJobThunk({
          jobName: job.job_name,
          dryRun: effectiveDryRun,
        })
      ).unwrap();
      onClose();
    } catch (err: unknown) {
      if (typeof err === 'string') {
        setSubmitError(err);
      } else if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: unknown }).message === 'string'
      ) {
        const { message, disabledReason } = err as {
          message: string;
          disabledReason?: string | null;
        };
        const suffix =
          disabledReason != null && disabledReason.length > 0
            ? ` (${disabledReason})`
            : '';
        setSubmitError(`${message}${suffix}`);
      } else {
        setSubmitError('Failed to run job');
      }
    }
  };

  if (job == null) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Run job now"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isRunning}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()} disabled={isRunning}>
            {isRunning ? 'Running…' : 'Run'}
          </Button>
        </>
      }
    >
      <div className="job-run-now-modal">
        <div className="job-run-now-modal__row">
          <span className="job-run-now-modal__label">Job</span>
          <span className="job-run-now-modal__value">{job.job_name}</span>
        </div>
        <div className="job-run-now-modal__row">
          <span className="job-run-now-modal__label">Schedule</span>
          <span className="job-run-now-modal__value job-run-now-modal__value--mono">{job.schedule}</span>
        </div>
        <div
          className="job-run-now-modal__checkbox-wrap"
          title={
            dryRunLocked ? 'Job is disabled — only dry-run is available' : undefined
          }
        >
          <label className="job-run-now-modal__checkbox-label">
            <input
              type="checkbox"
              checked={effectiveDryRun}
              disabled={dryRunLocked}
              onChange={(e) => setDryRun(e.target.checked)}
            />
            <span>Dry run</span>
          </label>
          {dryRunLocked && (
            <span className="job-run-now-modal__hint">
              Job is disabled — only dry-run is available
            </span>
          )}
        </div>
        {submitError != null && submitError.length > 0 && (
          <p className="job-run-now-modal__error" role="alert" data-testid="job-run-error">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default JobRunNowModal;
